import { create } from "zustand";
import { createSelectors } from "../stores/create-selectors.util";

export const initImagineRouting = ({
  initialRoute,
  onNavigate,
}: {
  initialRoute: ImagineRoute;
  onNavigate?: (route: ImagineRoute, animate?: boolean) => void;
}) => {
  imagineRoutingStore.setState({
    currentRoute: initialRoute,
    initialized: true,
    onNavigate,
  });

  return {
    navigate: imagineRoutingStore.getState().navigate,
  };
};

export const navigateToRoute = (route: ImagineRoute) => {
  if (!route) return;
  const { navigate, currentRoute } = imagineRoutingStore.getState();
  if (currentRoute && currentRoute.id === route.id) return;
  navigate(route);
};

export type ImagineRoute =
  | {
      id: "home";
      props: {};
    }
  | {
      id: "project";
      props: {
        projectId: string;
        region: string;
        isGeneratingNewProject?: boolean;
      };
    }
  | {
      id: "view";
      props: {
        projectId: string;
        region: string;
      };
    }
  | null;

type NavigateOptions = {
  animate: {
    promptSection: {
      delay: number;
    };
    projectsSection: {
      delay: number;
    };
    navigationDelay: number;
  };
};

const defaultNavigateOptions: NavigateOptions = {
  animate: {
    promptSection: {
      delay: 0,
    },
    projectsSection: {
      delay: 0,
    },
    navigationDelay: 0,
  },
};

type ImagineRoutingStoreState = {
  currentRoute: ImagineRoute;
  onNavigate?: (route: ImagineRoute) => void;
  initialized: boolean;
  showPromptSection: boolean;
  showProjectsSection: boolean;
  showStudioView: boolean;
  isAnimating: boolean;
};

type ImagineRoutingStoreActions = {
  navigate: (route: ImagineRoute, options?: NavigateOptions) => void;
};

type ImagineRoutingStore = ImagineRoutingStoreState &
  ImagineRoutingStoreActions;

export const ROUTING_ANIMATION_DELAY = 5000;

const defaultImagineRoutingStoreState: ImagineRoutingStoreState = {
  currentRoute: {
    id: "home",
    props: {},
  },
  initialized: false,
  showPromptSection: true,
  showProjectsSection: true,
  showStudioView: true,
  isAnimating: false,
};

export const imagineRoutingStore = createSelectors(
  create<ImagineRoutingStore>((set, get) => ({
    ...defaultImagineRoutingStoreState,
    navigate: (route, options) => {
      const onNavigate = get().onNavigate;
      const animate = options?.animate;
      const navigateFn = () => {
        set({
          currentRoute: route,
          showProjectsSection: route?.id === "home",
          showPromptSection: route?.id === "home",
          showStudioView: route?.id === "project" || route?.id === "view",
        });
        onNavigate?.(route);
      };
      if (animate) {
        const animatedNavigateFn = async () => {
          set({ isAnimating: true });
          await after(animate.projectsSection.delay);
          set({ showProjectsSection: false });
          await after(animate.promptSection.delay);
          set({ showPromptSection: false });
          await after(animate.navigationDelay);
          navigateFn();
          set({ isAnimating: false });
        };
        animatedNavigateFn();
      } else {
        navigateFn();
      }
    },
  })),
);

async function after(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
